import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Form, Alert } from 'react-bootstrap';
import { FaPlus, FaCopy } from 'react-icons/fa';
import { apiFetch } from '../api';
import { useAuth } from '../auth/AuthContext';
import { canManageProducts } from '../auth/roles';
import ProductEditModal from './ProductEditModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { PageHeader, QuantityProgress } from './ui';

function ProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: '', code: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [copyProduct, setCopyProduct] = useState(null); // для копирования

  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = canManageProducts(user?.role);

  const loadProducts = (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    return apiFetch('/api/products/?page_size=100')
      .then((data) => {
        setProducts(data.results || data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Не удалось загрузить список изделий');
        setLoading(false);
      });
  };

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    document.title = 'Изделия — Производство';
  }, []);

  const totalProducts = products.length;
  const totalQty = products.reduce((sum, p) => sum + (p.total || 0), 0);
  const doneQty = products.reduce((sum, p) => sum + (p.done || 0), 0);

  // Создание нового изделия (без копирования)
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('code', form.code.trim());
      if (imageFile) {
        formData.append('image', imageFile);
      }

      await apiFetch('/api/products/', {
        method: 'POST',
        body: formData,
      });

      setShowModal(false);
      setForm({ name: '', code: '' });
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadProducts(true);
    } catch (err) {
      setFormError(err.message || 'Ошибка создания');
    } finally {
      setSaving(false);
    }
  };

  // Копирование – открываем ProductEditModal в режиме копирования
  const handleCopyProduct = (product) => {
    setCopyProduct(product);
  };

  // Общий обработчик сохранения (после редактирования или копирования)
  const handleProductSaved = () => {
    setEditingProduct(null);
    setCopyProduct(null);
    loadProducts(true);
  };

  if (loading) return <div className="empty-state">Загрузка...</div>;
  if (error) return <div className="empty-state text-danger">{error}</div>;

  return (
    <>
      <PageHeader
        title="Изделия"
        subtitle="Общий прогресс по всем изделиям"
        actions={
          canManage && (
            <button
              type="button"
              className="app-btn app-btn-primary"
              onClick={() => setShowModal(true)}
            >
              <FaPlus />
            </button>
          )
        }
      />

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-card-label">Всего изделий</div>
          <div className="stat-card-value">{totalProducts}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Кол-во (сделано / всего)</div>
          <QuantityProgress done={doneQty} total={totalQty} />
        </div>
      </div>

      <div className="cards-grid">
        {products.map((product) => (
          <div
            key={product.id}
            className="app-card app-card-clickable"
            onClick={() => navigate(`/product/${product.id}/contracts`)}
          >
            <div className="app-card-body">
              {product.image && (
                <img
                  src={product.image}
                  alt={product.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    display: 'block',
                    marginBottom: '8px',
                    borderRadius: '4px',
                  }}
                />
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px', alignItems: 'start' }}>
                <div>
                  <div className="fw-semibold" style={{ fontSize: 16, wordWrap: 'break-word' }}>
                    {product.name}
                  </div>
                  <div className="app-text-muted small">{product.code}</div>
                </div>
                <span className="app-badge app-badge-neutral" style={{ whiteSpace: 'nowrap', alignSelf: 'start', marginTop: '2px' }}>
                  {product.contracts?.length || 0} дог.
                </span>
              </div>
              <QuantityProgress done={product.done || 0} total={product.total || 0} />
              {canManage && (
                <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="app-btn app-btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingProduct(product);
                    }}
                    title="Редактировать"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="app-btn app-btn-sm app-btn-danger-outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget({
                        id: product.id,
                        name: `${product.name} (${product.code})`,
                      });
                    }}
                    title="Удалить"
                  >
                    🗑
                  </button>
                  <button
                    type="button"
                    className="app-btn app-btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyProduct(product);
                    }}
                    title="Копировать изделие"
                  >
                    <FaCopy />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="empty-state">Нет изделий. {canManage && 'Нажмите «Новое изделие», чтобы добавить.'}</div>
      )}

      {/* Модалка для создания нового изделия (без копирования) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton className="app-surface">
          <Modal.Title>Новое изделие</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body className="app-surface">
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Название</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Код</Form.Label>
              <Form.Control
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Изображение</Form.Label>
              <div className="d-flex flex-column gap-2">
                {imagePreview && (
                  <div style={{ position: 'relative', maxWidth: '200px' }}>
                    <img
                      src={imagePreview}
                      alt="Превью"
                      style={{ width: '100%', height: 'auto', borderRadius: '6px' }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      style={{ position: 'absolute', top: '4px', right: '4px', borderRadius: '50%' }}
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setImageFile(file);
                        setImagePreview(URL.createObjectURL(file));
                      }
                    }}
                    className="form-control"
                  />
                  <small className="text-muted">Рекомендуемый размер: 400×300 px</small>
                </div>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="app-surface">
            <button type="button" className="app-btn" onClick={() => setShowModal(false)}>
              Отмена
            </button>
            <button type="submit" className="app-btn app-btn-primary" disabled={saving}>
              {saving ? 'Сохранение...' : 'Создать'}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Единый ProductEditModal для редактирования и копирования */}
      <ProductEditModal
        show={!!editingProduct || !!copyProduct}
        onHide={() => {
          setEditingProduct(null);
          setCopyProduct(null);
        }}
        product={editingProduct || copyProduct}
        isCopy={!!copyProduct} // флаг, указывающий, что это копирование
        onSaved={handleProductSaved}
        onDelete={
          canManage && editingProduct
            ? () => {
                setEditingProduct(null);
                setDeleteTarget({
                  id: editingProduct.id,
                  name: `${editingProduct.name} (${editingProduct.code})`,
                });
              }
            : undefined
        }
      />

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await apiFetch(`/api/products/${deleteTarget.id}/`, { method: 'DELETE' });
          setDeleteTarget(null);
          loadProducts(true);
        }}
        title="Удалить изделие?"
        message="Будут удалены все договоры, стадии и файлы, связанные с этим изделием."
        itemName={deleteTarget?.name}
      />
    </>
  );
}

export default ProductList;